import { supabase } from '../config/supabase';
import { TemplateType } from '../components/import/MonthlyDataImporter';
import { MonthlyStats } from '../types/property';
import { createMonthlyStats } from './stats/monthlyStats';
import { updatePropertyStats } from './stats/propertyStats';

// Interfaz para manejar la importación de datos
export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// Interfaz para manejar conflictos de importación
export interface ImportConflict {
  row: number;
  propertyId: string;
  propertyName: string;
  month: number;
  year: number;
  existing: MonthlyStats;
  new: Partial<MonthlyStats>;
}

// Función para limpiar y convertir valores monetarios (ej: "628 €" -> 628)
const cleanMonetaryValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  
  // Eliminar el símbolo de euro y cualquier espacio
  const cleanValue = value.replace(/[€\s]/g, '');
  // Convertir comas a puntos si es necesario
  const normalizedValue = cleanValue.replace(',', '.');
  // Convertir a número
  const numValue = parseFloat(normalizedValue);
  
  return isNaN(numValue) ? 0 : numValue;
};

// Función para limpiar y convertir porcentajes (ej: "64%" -> 64)
const cleanPercentageValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  
  // Eliminar el símbolo de porcentaje y cualquier espacio
  const cleanValue = value.replace(/[%\s]/g, '');
  // Convertir comas a puntos si es necesario
  const normalizedValue = cleanValue.replace(',', '.');
  // Convertir a número
  const numValue = parseFloat(normalizedValue);
  
  return isNaN(numValue) ? 0 : numValue;
};

// Función para verificar si ya existen registros para una propiedad, mes y año
export const checkExistingRecords = async (
  propertyId: string,
  month: number,
  year: number
): Promise<MonthlyStats | null> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .select('*')
    .eq('property_id', propertyId)
    .eq('month', month)
    .eq('year', year)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 es el código para "no se encontraron resultados"
    console.error('Error al verificar registros existentes:', error);
    throw new Error(`Error al verificar registros existentes: ${error.message}`);
  }

  if (!data) return null;

  // Convertir de snake_case (DB) a camelCase (TypeScript)
  return {
    id: data.id,
    propertyId: data.property_id,
    year: data.year,
    month: data.month,
    revenue: data.revenue,
    cleaningCosts: data.cleaning_costs,
    inmohostCosts: data.inmohost_costs,
    profit: data.profit,
    occupancyRate: data.occupancy_rate,
    reservations: data.reservations,
    averageNightPrice: data.average_night_price,
    averageStayDuration: data.average_stay_duration,
    created_at: data.created_at
  };
};

// Función para analizar datos de Excel y detectar conflictos
export const analyzeImportData = async (
  data: any[],
  templateType: TemplateType,
  options?: { propertyId?: string; month?: number; year?: number }
): Promise<{ data: Partial<MonthlyStats>[]; conflicts: ImportConflict[] }> => {
  if (!data || data.length === 0) {
    throw new Error('No hay datos para importar');
  }

  const processedData: Partial<MonthlyStats>[] = [];
  const conflicts: ImportConflict[] = [];

  // Procesar los datos según el tipo de plantilla
  switch (templateType) {
    case TemplateType.MONTHLY_BY_PROPERTY: {
      if (!options?.propertyId) {
        throw new Error('Se requiere un ID de propiedad para importar datos mensuales por propiedad');
      }

      // Verificar que la propiedad existe
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .select('id, name')
        .eq('id', options.propertyId)
        .single();

      if (propertyError || !propertyData) {
        throw new Error(`La propiedad con ID ${options.propertyId} no existe`);
      }

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        
        // Validar campos obligatorios
        if (!item['Mes'] || !item['Año']) {
          throw new Error(`Fila ${i + 1}: Mes y Año son obligatorios`);
        }

        // Convertir y validar campos
        const month = Number(item['Mes']);
        const year = Number(item['Año']);
        
        if (isNaN(month) || month < 1 || month > 12) {
          throw new Error(`Fila ${i + 1}: Mes debe ser un número entre 1 y 12`);
        }
        
        if (isNaN(year) || year < 2000 || year > 2100) {
          throw new Error(`Fila ${i + 1}: Año debe ser un número válido`);
        }

        // Convertir valores monetarios y porcentajes
        const revenue = cleanMonetaryValue(item['Facturación'] || 0);
        const cleaningCosts = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
        const inmohostCosts = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
        const profit = cleanMonetaryValue(item['Beneficio'] || 0);
        const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
        const reservations = Number(item['Reservas'] || 0);
        const averageNightPrice = cleanMonetaryValue(item['Precio Promedio'] || 0);
        const averageStayDuration = Number(item['Duración Estancia'] || 0);

        // Crear objeto con los datos validados (usando camelCase para TypeScript)
        const newData: Partial<MonthlyStats> = {
          propertyId: options.propertyId,
          month,
          year,
          revenue,
          cleaningCosts,
          inmohostCosts,
          profit,
          occupancyRate,
          reservations,
          averageNightPrice,
          averageStayDuration
        };

        // Verificar si ya existe un registro para esta propiedad, mes y año
        const existingRecord = await checkExistingRecords(options.propertyId, month, year);
        
        if (existingRecord) {
          conflicts.push({
            row: i + 1,
            propertyId: options.propertyId,
            propertyName: propertyData.name,
            month,
            year,
            existing: existingRecord,
            new: newData
          });
        }
        
        processedData.push(newData);
      }
      break;
    }
    
    case TemplateType.MULTIPLE_PROPERTIES: {
      if (!options?.month || !options?.year) {
        throw new Error('Se requiere mes y año para importar datos de múltiples propiedades');
      }

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        
        // Validar campos obligatorios
        if (!item['ID Propiedad'] || !item['Nombre Propiedad']) {
          throw new Error(`Fila ${i + 1}: ID Propiedad y Nombre Propiedad son obligatorios`);
        }

        const propertyId = item['ID Propiedad'];

        // Verificar que la propiedad existe
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select('id, name')
          .eq('id', propertyId)
          .single();

        if (propertyError || !propertyData) {
          throw new Error(`Fila ${i + 1}: La propiedad con ID ${propertyId} no existe`);
        }

        // Convertir valores monetarios y porcentajes
        const revenue = cleanMonetaryValue(item['Facturación'] || 0);
        const cleaningCosts = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
        const inmohostCosts = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
        const profit = cleanMonetaryValue(item['Beneficio'] || 0);
        const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
        const reservations = Number(item['Reservas'] || 0);
        const averageNightPrice = cleanMonetaryValue(item['Precio Promedio'] || 0);
        const averageStayDuration = Number(item['Duración Estancia'] || 0);

        // Crear objeto con los datos validados (usando camelCase para TypeScript)
        const newData: Partial<MonthlyStats> = {
          propertyId,
          month: options.month,
          year: options.year,
          revenue,
          cleaningCosts,
          inmohostCosts,
          profit,
          occupancyRate,
          reservations,
          averageNightPrice,
          averageStayDuration
        };

        // Verificar si ya existe un registro para esta propiedad, mes y año
        const existingRecord = await checkExistingRecords(propertyId, options.month, options.year);
        
        if (existingRecord) {
          conflicts.push({
            row: i + 1,
            propertyId,
            propertyName: propertyData.name,
            month: options.month,
            year: options.year,
            existing: existingRecord,
            new: newData
          });
        }
        
        processedData.push(newData);
      }
      break;
    }
    
    case TemplateType.FULL_HISTORY: {
      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        
        // Validar campos obligatorios
        if (!item['ID Propiedad'] || !item['Nombre Propiedad'] || !item['Mes'] || !item['Año']) {
          throw new Error(`Fila ${i + 1}: ID Propiedad, Nombre Propiedad, Mes y Año son obligatorios`);
        }

        const propertyId = item['ID Propiedad'];
        const month = Number(item['Mes']);
        const year = Number(item['Año']);

        // Verificar que la propiedad existe
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select('id, name')
          .eq('id', propertyId)
          .single();

        if (propertyError || !propertyData) {
          throw new Error(`Fila ${i + 1}: La propiedad con ID ${propertyId} no existe`);
        }

        // Validar mes y año
        if (isNaN(month) || month < 1 || month > 12) {
          throw new Error(`Fila ${i + 1}: Mes debe ser un número entre 1 y 12`);
        }
        
        if (isNaN(year) || year < 2000 || year > 2100) {
          throw new Error(`Fila ${i + 1}: Año debe ser un número válido`);
        }

        // Convertir valores monetarios y porcentajes
        const revenue = cleanMonetaryValue(item['Facturación'] || 0);
        const cleaningCosts = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
        const inmohostCosts = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
        const profit = cleanMonetaryValue(item['Beneficio'] || 0);
        const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
        const reservations = Number(item['Reservas'] || 0);
        const averageNightPrice = cleanMonetaryValue(item['Precio Promedio'] || 0);
        const averageStayDuration = Number(item['Duración Estancia'] || 0);

        // Crear objeto con los datos validados (usando camelCase para TypeScript)
        const newData: Partial<MonthlyStats> = {
          propertyId,
          month,
          year,
          revenue,
          cleaningCosts,
          inmohostCosts,
          profit,
          occupancyRate,
          reservations,
          averageNightPrice,
          averageStayDuration
        };

        // Verificar si ya existe un registro para esta propiedad, mes y año
        const existingRecord = await checkExistingRecords(propertyId, month, year);
        
        if (existingRecord) {
          conflicts.push({
            row: i + 1,
            propertyId,
            propertyName: propertyData.name,
            month,
            year,
            existing: existingRecord,
            new: newData
          });
        }
        
        processedData.push(newData);
      }
      break;
    }
    
    default:
      throw new Error('Tipo de plantilla no válido');
  }

  return { data: processedData, conflicts };
};

// Función para importar datos mensuales
export const importMonthlyData = async (
  processedData: Partial<MonthlyStats>[],
  conflictResolution: 'update' | 'skip' | 'create_new' = 'skip'
): Promise<ImportResult> => {
  const result: ImportResult = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: []
  };

  for (const item of processedData) {
    try {
      // Verificar si ya existe un registro para esta propiedad, mes y año
      const existingRecord = await checkExistingRecords(
        item.propertyId!,
        item.month!,
        item.year!
      );
      
      if (existingRecord) {
        // Manejar según la estrategia de resolución de conflictos
        if (conflictResolution === 'update') {
          // Actualizar el registro existente
          const { error } = await supabase
            .from('monthly_stats')
            .update({
              revenue: item.revenue,
              cleaning_costs: item.cleaningCosts,
              inmohost_costs: item.inmohostCosts,
              profit: item.profit,
              occupancy_rate: item.occupancyRate,
              reservations: item.reservations,
              average_night_price: item.averageNightPrice,
              average_stay_duration: item.averageStayDuration
            })
            .eq('id', existingRecord.id);

          if (error) {
            result.errors.push(`Error al actualizar registro: ${error.message}`);
          } else {
            result.updated++;
            // Actualizar las estadísticas de la propiedad
            await updatePropertyStats(item.propertyId!);
          }
        } else if (conflictResolution === 'create_new') {
          // Crear un nuevo registro (esto podría causar duplicados)
          await createMonthlyStats({
            propertyId: item.propertyId!,
            year: item.year!,
            month: item.month!,
            revenue: item.revenue!,
            cleaningCosts: item.cleaningCosts!,
            inmohostCosts: item.inmohostCosts!,
            profit: item.profit!,
            occupancyRate: item.occupancyRate!,
            reservations: item.reservations!,
            averageNightPrice: item.averageNightPrice!,
            averageStayDuration: item.averageStayDuration!
          });
          result.created++;
        } else {
          // Omitir el registro
          result.skipped++;
        }
      } else {
        // Crear un nuevo registro
        await createMonthlyStats({
          propertyId: item.propertyId!,
          year: item.year!,
          month: item.month!,
          revenue: item.revenue!,
          cleaningCosts: item.cleaningCosts!,
          inmohostCosts: item.inmohostCosts!,
          profit: item.profit!,
          occupancyRate: item.occupancyRate!,
          reservations: item.reservations!,
          averageNightPrice: item.averageNightPrice!,
          averageStayDuration: item.averageStayDuration!
        });
        result.created++;
      }
    } catch (error: any) {
      result.errors.push(error.message);
    }
  }

  return result;
};

// Función para obtener datos mensuales filtrados por año
export const getMonthlyDataByYear = async (propertyId: string, year?: number): Promise<MonthlyStats[]> => {
  let query = supabase
    .from('monthly_stats')
    .select('*')
    .eq('property_id', propertyId)
    .order('year', { ascending: false })
    .order('month', { ascending: false });
  
  if (year) {
    query = query.eq('year', year);
  }
  
  const { data, error } = await query;

  if (error) {
    console.error('Error al obtener datos mensuales:', error);
    throw new Error(`Error al obtener datos mensuales: ${error.message}`);
  }

  // Convertir de snake_case (DB) a camelCase (TypeScript)
  return data.map(item => ({
    id: item.id,
    propertyId: item.property_id,
    year: item.year,
    month: item.month,
    revenue: item.revenue,
    cleaningCosts: item.cleaning_costs,
    inmohostCosts: item.inmohost_costs,
    profit: item.profit,
    occupancyRate: item.occupancy_rate,
    reservations: item.reservations,
    averageNightPrice: item.average_night_price,
    averageStayDuration: item.average_stay_duration,
    created_at: item.created_at
  }));
};

// Función para obtener años disponibles para una propiedad
export const getAvailableYears = async (propertyId: string): Promise<number[]> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .select('year')
    .eq('property_id', propertyId)
    .order('year', { ascending: false });

  if (error) {
    console.error('Error al obtener años disponibles:', error);
    throw new Error(`Error al obtener años disponibles: ${error.message}`);
  }

  // Extraer años únicos
  const years = [...new Set(data.map(item => item.year))];
  return years;
};
